import { useEffect } from 'react'
import { motion } from 'framer-motion'
import cn from 'utils/cn'
import Button from 'core/components/buttons/Button'
import Dialog, { DialogProps } from 'core/components/dialogs/Dialog'

export interface ModalProps extends Omit<DialogProps, 'title'> {
  /**
   * Optional text displayed above the main title.
   */
  surTitle?: string
  /**
   * Main title of the modal dialog.
   */
  title?: React.ReactNode
  /**
   * Content to display in the modal footer.
   */
  footer?: React.ReactNode
  /**
   * Custom close button element for the modal.
   */
  closeButton?: React.ReactNode
  /**
   * Callback fired when the back button is pressed.
   */
  onBack?: () => void
  /**
   * Additional class names for the modal dialog container.
   */
  dialogClassName?: string
  /**
   * Controls whether the dialog is visible.
   */
  show?: boolean
  /**
   * Callback fired when the dialog requests to close (e.g. clicking backdrop or pressing Escape).
   */
  onClose?: VoidFunction
  /**
   * Whether to show a backdrop behind the dialog.
   */
  hasBackdrop?: boolean
}

const Modal = ({
  show,
  surTitle,
  title,
  children,
  footer,
  className,
  dialogClassName = '',
  closeButton,
  hasBackdrop = true,
  onClose = console.log,
  onBack,
  ...rest
}: ModalProps) => {
  useEffect(() => {
    if (!show) return
    const body = document.getElementsByTagName('body')?.[0]
    body?.classList?.add('overflow-hidden')
    return () => {
      body?.classList?.remove('overflow-hidden')
    }
  }, [show])

  return (
    <Dialog
      id="core-modal"
      show={show}
      className={cn(
        'left-0 right-0 top-14 flex justify-center items-center pointer-events-none',
        dialogClassName
      )}
      onClose={onClose}
      hasBackdrop={hasBackdrop}
      motionProps={{
        open: {
          opacity: 1,
          y: 0
        },
        closed: {
          opacity: 0,
          y: -50
        }
      }}
      {...rest}
    >
      <motion.div
        className={cn(
          'inline-block card relative bg-white w-[90vw] sm:w-138.75 max-h-[80vh] max-w-[94vw] overflow-auto transition-all pointer-events-auto',
          className
        )}
      >
        {onBack ? (
          <>
            <div
              data-testid="modal-close-button-container"
              className="flex justify-between items-center mb-5"
            >
              <Button
                icon="chevron-left"
                onClick={onBack}
                className="p-0! hover:bg-white!"
              >
                Back
              </Button>
              {closeButton ?? (
                <Button
                  data-testid="modal-close-button"
                  icon="close"
                  onClick={onClose}
                  className="p-0! hover:bg-white!"
                />
              )}
            </div>
            {surTitle && (
              <div className="text-sm text-dark-3 font-bold">{surTitle}</div>
            )}
          </>
        ) : (
          <>
            {surTitle && (
              <div className="absolute left-8 top-4 text-sm text-dark-3 font-bold">
                {surTitle}
              </div>
            )}
            <div
              data-testid="modal-close-button-container"
              className="absolute right-2 top-2 z-50 flex items-center"
            >
              {closeButton ?? (
                <Button
                  data-testid="modal-close-button"
                  icon="close"
                  onClick={onClose}
                />
              )}
            </div>
          </>
        )}
        {title && typeof title === 'string' && (
          <p
            className={cn(
              'text-2xl font-bold m-0 mb-4',
              surTitle ? 'mt-3' : ''
            )}
          >
            {title}
          </p>
        )}
        {title && typeof title !== 'string' && <span>{title}</span>}
        {children}
        {footer && (
          <div className="flex justify-end items-center mt-6 -mb-2">
            {footer}
          </div>
        )}
      </motion.div>
    </Dialog>
  )
}

export default Modal
